import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Radio, Row, Select, Space, Statistic, Table, Typography, } from 'antd';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { api, apiError } from '@/lib/api';
import { formatRupiah } from '@/lib/format';
let keyCounter = 1;
const nextKey = () => keyCounter++;
export function PurchaseEntryPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [txDate, setTxDate] = useState(dayjs());
    const [supplier, setSupplier] = useState('');
    const [refNo, setRefNo] = useState('');
    const [payment, setPayment] = useState('CASH');
    const [notes, setNotes] = useState('');
    const [lines, setLines] = useState([{ key: nextKey(), qty: 1, unit_cost: 0 }]);
    const products = useQuery({
        queryKey: ['toko-products-select'],
        queryFn: async () => (await api.get('/toko/products', { params: { active: true } })).data,
    });
    const total = useMemo(() => lines.reduce((s, l) => s + l.qty * l.unit_cost, 0), [lines]);
    const submitMut = useMutation({
        mutationFn: async () => {
            const body = {
                tx_date: txDate.format('YYYY-MM-DD'),
                payment_method: payment,
                supplier: supplier || null,
                reference_no: refNo || null,
                notes: notes || null,
                items: lines.map((l) => ({
                    product_id: l.product_id,
                    qty: String(l.qty),
                    unit_cost: String(l.unit_cost),
                })),
            };
            return (await api.post('/toko/purchase', body)).data;
        },
        onSuccess: (r) => {
            message.success('Pembelian dicatat & jurnal diposting');
            qc.invalidateQueries({ queryKey: ['toko-products'] });
            navigate(`/accounting/journals/${r.journal_id}`);
        },
        onError: (e) => message.error(apiError(e)),
    });
    const cols = [
        {
            title: 'Produk',
            width: 340,
            render: (_, r) => (_jsx(Select, { showSearch: true, value: r.product_id, placeholder: "Pilih produk", style: { width: '100%' }, optionFilterProp: "label", onChange: (v) => setLines((ls) => ls.map((l) => l.key === r.key
                    ? {
                        ...l,
                        product_id: v,
                        unit_cost: parseFloat((products.data ?? []).find((p) => p.id === v)?.stock_avg_cost || '0'),
                    }
                    : l)), options: (products.data ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` })) })),
        },
        {
            title: 'Qty',
            width: 130,
            render: (_, r) => (_jsx(InputNumber, { min: 0, step: 0.1, value: r.qty, onChange: (v) => setLines((ls) => ls.map((l) => (l.key === r.key ? { ...l, qty: v ?? 0 } : l))), style: { width: '100%' } })),
        },
        {
            title: 'Harga Beli',
            width: 180,
            align: 'right',
            render: (_, r) => (_jsx(InputNumber, { min: 0, value: r.unit_cost, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0), onChange: (v) => setLines((ls) => ls.map((l) => (l.key === r.key ? { ...l, unit_cost: v ?? 0 } : l))), style: { width: '100%' } })),
        },
        {
            title: 'Subtotal',
            width: 180,
            align: 'right',
            render: (_, r) => (_jsx("span", { className: "money", children: formatRupiah(r.qty * r.unit_cost) })),
        },
        {
            title: '',
            width: 40,
            render: (_, r) => (_jsx(Button, { type: "text", danger: true, icon: _jsx(DeleteOutlined, {}), onClick: () => setLines((ls) => ls.filter((l) => l.key !== r.key)) })),
        },
    ];
    const canSubmit = lines.length > 0 &&
        lines.every((l) => l.product_id && l.qty > 0 && l.unit_cost >= 0);
    return (_jsxs("div", { children: [_jsx(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between' }, children: _jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Input Pembelian" }) }), _jsx(Card, { style: { marginBottom: 16 }, children: _jsxs(Row, { gutter: 16, children: [_jsx(Col, { span: 6, children: _jsx(Form.Item, { label: "Tanggal", style: { marginBottom: 0 }, children: _jsx(DatePicker, { value: txDate, onChange: (v) => setTxDate(v ?? dayjs()), style: { width: '100%' } }) }) }), _jsx(Col, { span: 6, children: _jsx(Form.Item, { label: "Metode", style: { marginBottom: 0 }, children: _jsxs(Radio.Group, { value: payment, onChange: (e) => setPayment(e.target.value), children: [_jsx(Radio.Button, { value: "CASH", children: "Kas" }), _jsx(Radio.Button, { value: "CREDIT", children: "Utang" })] }) }) }), _jsx(Col, { span: 6, children: _jsx(Form.Item, { label: "Supplier", style: { marginBottom: 0 }, children: _jsx(Input, { value: supplier, onChange: (e) => setSupplier(e.target.value) }) }) }), _jsx(Col, { span: 6, children: _jsx(Form.Item, { label: "Nomor Faktur", style: { marginBottom: 0 }, children: _jsx(Input, { value: refNo, onChange: (e) => setRefNo(e.target.value) }) }) })] }) }), _jsx(Card, { title: "Item Pembelian", style: { marginBottom: 16 }, extra: _jsx(Button, { icon: _jsx(PlusOutlined, {}), onClick: () => setLines((ls) => [...ls, { key: nextKey(), qty: 1, unit_cost: 0 }]), children: "Tambah Baris" }), children: _jsx(Table, { rowKey: "key", dataSource: lines, columns: cols, pagination: false, size: "small" }) }), _jsx(Row, { gutter: 16, style: { marginBottom: 16 }, children: _jsx(Col, { span: 8, children: _jsx(Card, { children: _jsx(Statistic, { title: "Total Pembelian", value: total, formatter: (v) => formatRupiah(Number(v)) }) }) }) }), _jsxs(Space, { children: [_jsx(Input.TextArea, { placeholder: "Catatan\u2026", rows: 2, value: notes, onChange: (e) => setNotes(e.target.value), style: { width: 320 } }), _jsx(Button, { type: "primary", icon: _jsx(SendOutlined, {}), disabled: !canSubmit || total <= 0, loading: submitMut.isPending, onClick: () => submitMut.mutate(), children: "Post Pembelian" })] })] }));
}
